import type { IconType } from 'react-icons';
import { FaPlug } from 'react-icons/fa';
import {
  FiActivity,
  FiBarChart2,
  FiClock,
  FiFolder,
  FiGitMerge,
  FiInbox,
  FiKey,
  FiSliders,
  FiImage,
  FiUser,
  FiUsers,
  FiUserCheck,
} from 'react-icons/fi';
import { PERMISSIONS, hasPermission } from './roleUtils';
import { WORKFLOW_QUEUES } from './workflowQueues';
import { DMS_APP_URL, isDmsEnabled } from './dmsIntegration';

/**
 * Every destination in the application, with the permission that reveals it.
 *
 * The sidebar and the command palette both read this, so a screen can never appear
 * in one and not the other — and a new route needs adding in exactly one place.
 */
export interface NavDestination {
  path: string;
  label: string;
  /** Omitted for destinations everyone can reach. */
  permission?: string;
  /** The area it belongs to: a sidebar section and the palette's context hint. */
  group: NavGroup;
  /** Nested heading under the group. A label, not a route. */
  section?: string;
  icon: IconType;
  /** Opens in a new tab instead of a route in this app. */
  external?: boolean;
  /** Extra search terms. Never displayed. */
  keywords?: string;
}

/**
 * Grouped by what a person is doing, not by which API owns it: working the
 * queue, shaping the process, managing the people who do it, and administration.
 */
export type NavGroup = 'Work' | 'Apps' | 'Process' | 'People' | 'Admin';

export const NAV_GROUPS: NavGroup[] = ['Work', 'Apps', 'Process', 'People', 'Admin'];

export const NAV_DESTINATIONS: NavDestination[] = [
  { path: '/dashboard', label: 'Dashboard', permission: PERMISSIONS.dashboardView, group: 'Work', icon: FiBarChart2, keywords: 'overview open working due overdue completion' },
  { path: '/enquiry', label: 'Enquiries', permission: PERMISSIONS.enquiryView, group: 'Work', icon: FiInbox, keywords: 'enquiries tasks queue my work inbox' },
  ...WORKFLOW_QUEUES.map((queue) => ({
    path: queue.path,
    label: queue.title,
    permission: queue.permission,
    group: 'Work' as const,
    icon: FiInbox,
    keywords: `${queue.itemName} ${queue.workflowName}`,
  })),
  { path: DMS_APP_URL, label: 'DMS App', permission: PERMISSIONS.appView, group: 'Apps', icon: FiFolder, external: true, keywords: 'dms documents nuostack app' },
  { path: '/workflows', label: 'Workflows', permission: PERMISSIONS.workflowsView, group: 'Process', icon: FiGitMerge, keywords: 'stages process flow' },
  { path: '/sla-configuration', label: 'SLA Targets', permission: PERMISSIONS.slaView, group: 'Process', icon: FiClock, keywords: 'sla configuration deadlines response time' },
  { path: '/priority-rules', label: 'Priority Rules', permission: PERMISSIONS.priorityRulesView, group: 'Process', icon: FiSliders, keywords: 'critical high urgency conditions' },
  { path: '/teams', label: 'Teams', permission: PERMISSIONS.teamsView, group: 'People', icon: FiUsers, keywords: 'groups' },
  { path: '/members', label: 'Members', permission: PERMISSIONS.membersView, group: 'People', icon: FiUser, keywords: 'people staff engineers' },
  { path: '/workload-configuration', label: 'Workload', permission: PERMISSIONS.workloadView, group: 'People', icon: FiActivity, keywords: 'workload configuration capacity assignment balance' },
  { path: '/users', label: 'Users', permission: PERMISSIONS.usersView, group: 'Admin', section: 'User Management', icon: FiUserCheck, keywords: 'accounts logins' },
  { path: '/roles-permissions', label: 'Roles & Permissions', permission: PERMISSIONS.rolesView, group: 'Admin', section: 'User Management', icon: FiKey, keywords: 'access rights' },
  { path: '/integrations', label: 'Integration', permission: PERMISSIONS.usersView, group: 'Admin', icon: FaPlug, keywords: 'gmail email inbox mailbox connect' },
  { path: '/company-profile', label: 'Company profile', permission: PERMISSIONS.rolesView, group: 'Admin', icon: FiImage, keywords: 'branding logo company organization' },
];

/** A section heading is itself a permission. The pages inside still need their own. */
const GROUP_PERMISSION: Partial<Record<NavGroup, string>> = {
  Apps: PERMISSIONS.appView,
  People: PERMISSIONS.peopleView,
  Admin: PERMISSIONS.adminView,
};

/** The destinations this session may actually reach. */
export const visibleDestinations = (
  permissions: string[] | null,
  hasPermission: (permissions: string[] | null, code: string) => boolean,
  dmsEnabled = isDmsEnabled()
) => NAV_DESTINATIONS.filter((d) => {
  if (d.external && !dmsEnabled) return false;
  const groupPermission = GROUP_PERMISSION[d.group];
  if (groupPermission && !hasPermission(permissions, groupPermission)) return false;
  return !d.permission || hasPermission(permissions, d.permission);
});

/** First in-app page this session can open. External apps are not a landing page. */
export const landingPath = (permissions: string[] | null, dmsEnabled = isDmsEnabled()) =>
  visibleDestinations(permissions, hasPermission, dmsEnabled).find((destination) => !destination.external)?.path;
